import { useReveal } from "@/hooks/useReveal";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  TrendingUp,
  Wallet,
  Building2,
  Clock,
  Lightbulb,
  Users,
  GraduationCap,
  Leaf,
  Award,
  Heart,
  Rocket,
  Handshake,
  Mail,
  FileCheck,
  Phone,
  Video,
  MapPin,
  CalendarCheck,
  FileSignature,
  Hourglass,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import team1 from "@/assets/karriere/team-1.webp";
import team2 from "@/assets/karriere/team-2.webp";
import team3 from "@/assets/karriere/team-3.webp";
import team4 from "@/assets/karriere/team-4.webp";
import team5 from "@/assets/karriere/team-5.webp";
import team6 from "@/assets/karriere/team-6.webp";
import heroImage from "@/assets/karriere/hero.webp";

const APPLY_URL = "mailto:moin@finable.de";

const reasons = [
  { icon: Sparkles, title: "Sinnvolle Arbeit mit Impact", text: "Gestalte die Zukunft der Finanzwelt nachhaltig und transparent mit." },
  { icon: TrendingUp, title: "Karriere mit Entwicklungsmöglichkeiten", text: "Wir fördern Deine Talente und bieten Dir echte Aufstiegschancen." },
  { icon: Wallet, title: "Attraktive Vergütung & Benefits", text: "Leistung soll sich lohnen – faires Gehalt plus attraktive Zusatzleistungen." },
  { icon: Building2, title: "Moderne Arbeitsumgebung", text: "Ob Büro oder Remote – arbeite dort, wo Du am produktivsten bist." },
  { icon: Clock, title: "Flexibilität & Work-Life-Balance", text: "Flexible Arbeitszeiten für eine optimale Vereinbarkeit von Beruf und Privatleben." },
  { icon: Lightbulb, title: "Innovative Unternehmenskultur", text: "Flache Hierarchien, offene Kommunikation und kreative Ideen." },
  { icon: Users, title: "Teamgeist & Zusammenarbeit", text: "Erfolg entsteht gemeinsam – wertschätzende und unterstützende Teamkultur." },
  { icon: GraduationCap, title: "Weiterbildung & Coaching", text: "Wir investieren in Deine persönliche und fachliche Entwicklung." },
  { icon: Leaf, title: "Nachhaltigkeit & Verantwortung", text: "Wir schaffen finanziellen und gesellschaftlichen Mehrwert." },
];

const values = [
  { icon: Award, title: "Höchste Exzellenz", text: "Unsere Arbeit steht für höchste Qualität in jedem Detail. Unser Ziel ist es, Erwartungen nicht nur zu erfüllen, sondern zu übertreffen – mit Präzision, Verlässlichkeit und exzellenten Ergebnissen." },
  { icon: Heart, title: "Vertrautheit", text: "Ein Arbeitsumfeld, das von Vertrauen und Unterstützung geprägt ist. Jeder Einzelne ist Teil einer wertschätzenden Gemeinschaft, in der offene Kommunikation und Zusammenarbeit im Mittelpunkt stehen." },
  { icon: Rocket, title: "Fortschritt", text: "Innovation bei uns bedeutet, Zukunft aktiv zu gestalten, kreative Lösungen zu entwickeln und persönlich zu wachsen." },
  { icon: Handshake, title: "Treue", text: "Loyalität und Teamgeist stehen bei uns im Fokus – wir schaffen ein vertrauensvolles, unterstützendes Umfeld für gemeinsamen Erfolg." },
];

const steps = [
  { icon: Mail, title: "Bewerbungseingang", time: "1–2 Werktage", text: "Du sendest uns Deine Unterlagen – wir bestätigen den Eingang innerhalb von 1–2 Werktagen." },
  { icon: Phone, title: "Telefonisches Erstgespräch", time: "ca. 1 Woche", text: "Ein lockeres Kennenlernen am Telefon – wir klären gegenseitige Erwartungen." },
  { icon: Video, title: "Digitales Vorstellungsgespräch", time: "ca. 1–2 Wochen", text: "Per Videocall lernen wir uns intensiver kennen und besprechen Deine Rolle." },
  { icon: MapPin, title: "Vor-Ort-Vorstellungsgespräch", time: "max. 2 Wochen", text: "Du besuchst uns in Hamburg, lernst das Team und unsere Räumlichkeiten kennen." },
  { icon: CalendarCheck, title: "Probetag", time: "ca. 1 Woche", text: "Erlebe einen typischen Arbeitstag bei FINABLE und entscheide selbst, ob es passt." },
  { icon: FileSignature, title: "Vertragsangebot", time: "Willkommen!", text: "Du erhältst Dein Vertragsangebot – wir freuen uns auf Dich im Team." },
];

const faqs = [
  {
    q: "Welche Voraussetzungen sollte ich für eine Bewerbung mitbringen?",
    a: "Wichtig sind uns vor allem Dein Charakter, Deine Lernbereitschaft und Dein Interesse an Finanzthemen. Eine abgeschlossene Ausbildung oder ein Studium sind hilfreich, aber kein Muss – auch Quereinsteiger sind bei uns herzlich willkommen.",
  },
  {
    q: "Welche Unterlagen benötigt ihr für meine Bewerbung?",
    a: "Wir freuen uns über Deinen Lebenslauf, ein kurzes Anschreiben sowie relevante Zeugnisse. Ein klassisches Bewerbungsfoto ist nicht erforderlich – uns ist wichtiger, wer Du bist.",
  },
  {
    q: "Bietet ihr auch Praktika, Werkstudentenstellen oder Ausbildungsplätze an?",
    a: "Ja, wir bieten regelmäßig Praktika, Werkstudententätigkeiten sowie Ausbildungs- und Traineeprogramme an. Sprich uns einfach an – auch Initiativbewerbungen sind jederzeit willkommen.",
  },
  {
    q: "Ist Remote-Arbeit oder Homeoffice möglich?",
    a: "Ja. Wir bieten flexible Arbeitsmodelle mit einer Mischung aus Büroarbeit in Hamburg und Homeoffice. Die genaue Aufteilung besprechen wir individuell mit Dir.",
  },
  {
    q: "Wie lange dauert der gesamte Bewerbungsprozess?",
    a: "Vom ersten Kontakt bis zum Vertragsangebot vergehen in der Regel 6 bis 8 Wochen. Selbstverständlich halten wir Dich in jeder Phase auf dem Laufenden.",
  },
  {
    q: "Wie bereite ich mich am besten auf das Vorstellungsgespräch vor?",
    a: "Sei einfach Du selbst. Informiere Dich vorab über FINABLE, unsere Werte und unsere Leistungen. Überlege Dir, warum Du zu uns passt und welche Fragen Du an uns hast.",
  },
  {
    q: "Bietet ihr Weiterbildungs- und Entwicklungsmöglichkeiten?",
    a: "Definitiv. Wir investieren in Deine fachliche und persönliche Entwicklung – von internen Schulungen über Coachings bis hin zu IHK-Qualifikationen und externen Fortbildungen.",
  },
  {
    q: "Was passiert, wenn aktuell keine passende Stelle ausgeschrieben ist?",
    a: "Wir freuen uns immer über Initiativbewerbungen. Sende uns Deine Unterlagen an moin@finable.de – wenn Du zu uns passt, finden wir gemeinsam den richtigen Platz für Dich.",
  },
];

const galleryImages = [
  { src: team1, alt: "FINABLE Team bei der Arbeit", position: "object-center" },
  { src: team2, alt: "Kollegen im Austausch", position: "object-center" },
  { src: team3, alt: "Teamevent FINABLE", position: "object-center" },
  { src: team4, alt: "FINABLE Büro Hamburg", position: "object-right" },
  { src: team5, alt: "Konzentrierte Arbeit am Vertrag", position: "object-top" },
  { src: team6, alt: "Workshop bei FINABLE", position: "object-left-bottom" },
];

export default function Karriere() {
  useReveal();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Karriere bei FINABLE – Digitale Finanzberatung bundesweit"
        description="Werde Teil eines modernen, digitalen Finanzberatungsunternehmens. Jetzt bewerben."
        canonical="https://finable.de/karriere"
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="absolute top-1/4 -right-20 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
          <div className="container relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <span className="reveal inline-block text-accent font-semibold uppercase tracking-wider text-sm">
                Karriere bei FINABLE
              </span>
              <h1 className="reveal mt-4 font-display text-4xl md:text-6xl lg:text-6xl xl:text-7xl font-extrabold text-balance leading-[1.05]">
                Talente gewinnen.<br />
                Mitarbeiter begeistern.<br />
                <span className="text-accent">Gemeinsam wachsen.</span>
              </h1>
              <p className="reveal mt-8 text-lg md:text-xl text-white/80 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Bei FINABLE setzen wir uns täglich dafür ein, dass Menschen ihre finanzielle Zukunft transparent, nachhaltig und selbstbestimmt gestalten können. Werde Teil eines Teams, das Finanzberatung neu denkt.
              </p>
              <div className="reveal mt-10">
                <Button asChild size="lg" variant="secondary" className="text-base px-8 py-6 h-auto">
                  <a href={APPLY_URL}>Jetzt bewerben</a>
                </Button>
              </div>
            </div>
            <div className="reveal relative hidden lg:block">
              <div className="absolute -inset-4 bg-accent/20 rounded-3xl blur-2xl" aria-hidden />
              <div className="relative aspect-[4/5] lg:aspect-[5/6] rounded-3xl overflow-hidden shadow-elegant ring-1 ring-white/10">
                <img
                  src={heroImage}
                  alt="Beratungsgespräch im FINABLE Büro"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reasons */}
        <section className="py-20 md:py-28 bg-background">
          <div className="container">
            <div className="text-center max-w-3xl mx-auto reveal">
              <span className="text-accent font-semibold uppercase tracking-wider text-sm">Deine Chance bei uns</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                9 überzeugende Gründe, Dich bei uns zu bewerben
              </h2>
            </div>

            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reasons.map(({ icon: Icon, title, text }, i) => (
                <div
                  key={title}
                  className="reveal group relative bg-card border border-border rounded-2xl p-7 hover:shadow-elegant hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="absolute top-6 right-6 text-7xl font-display font-bold text-accent/10 leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-5 group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-display text-xl font-bold">{title}</h3>
                    <p className="mt-2 text-muted-foreground leading-relaxed">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Gallery */}
        <section className="py-20 md:py-28 bg-muted/30">
          <div className="container">
            <div className="text-center max-w-3xl mx-auto reveal">
              <span className="text-accent font-semibold uppercase tracking-wider text-sm">Einblicke</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                So arbeiten wir bei FINABLE
              </h2>
              <p className="mt-5 text-lg text-muted-foreground">
                Echte Momente aus unserem Alltag – Team, Beratung und gemeinsame Erlebnisse.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {galleryImages.map(({ src, alt, position }) => (
                <div
                  key={src}
                  className="reveal aspect-square overflow-hidden rounded-2xl bg-card group"
                >
                  <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className={`w-full h-full object-cover ${position ?? "object-center"} group-hover:scale-105 transition-transform duration-500`}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 md:py-28 bg-background relative overflow-hidden">
          <div className="container relative">
            <div className="text-center max-w-3xl mx-auto reveal">
              <span className="text-accent font-semibold uppercase tracking-wider text-sm">Unsere Werte</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                Was uns als Team ausmacht
              </h2>
            </div>

            <div className="mt-14 grid md:grid-cols-2 gap-6">
              {values.map(({ icon: Icon, title, text }) => (
                <div
                  key={title}
                  className="reveal bg-card border border-border rounded-2xl p-8 md:p-10 hover:shadow-elegant transition-all"
                >
                  <div className="flex items-start gap-5">
                  <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-hero text-white flex items-center justify-center shadow-elegant">
                      <Icon className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl font-bold uppercase tracking-wide">{title}</h3>
                      <p className="mt-3 text-muted-foreground leading-relaxed">{text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process – vertical timeline */}
        <section className="py-20 md:py-28 bg-muted/30">
          <div className="container">
            <div className="text-center max-w-3xl mx-auto reveal">
              <span className="text-accent font-semibold uppercase tracking-wider text-sm">Bewerbungsprozess</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                So läuft Deine Bewerbung ab
              </h2>
              <p className="mt-5 text-lg text-muted-foreground">
                Ein transparenter Prozess in sechs Schritten – von der ersten E-Mail bis zum Vertragsangebot.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-5 py-2 text-sm font-semibold">
                <Hourglass className="w-4 h-4" />
                Gesamtdauer: ca. 6–8 Wochen
              </div>
            </div>

            {/* Timeline */}
            <div className="relative mt-16 max-w-4xl mx-auto">
              {/* vertical line */}
              <div
                aria-hidden
                className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-accent/40 via-accent/30 to-accent/10 md:-translate-x-1/2"
              />

              <ol className="space-y-10 md:space-y-14">
                {steps.map(({ icon: Icon, title, time, text }, i) => {
                  const isLeft = i % 2 === 0;
                  return (
                    <li key={title} className="reveal relative md:grid md:grid-cols-2 md:gap-12 items-center">
                      {/* Node on line */}
                      <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-hero text-white flex items-center justify-center font-display font-bold shadow-elegant ring-4 ring-background">
                          {i + 1}
                        </div>
                      </div>

                      {/* Card */}
                      <div
                        className={[
                          "ml-20 md:ml-0",
                          isLeft ? "md:col-start-1 md:pr-16 md:text-right" : "md:col-start-2 md:pl-16",
                        ].join(" ")}
                      >
                        <div
                          className={[
                            "inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent mb-2",
                            isLeft ? "md:flex-row-reverse" : "",
                          ].join(" ")}
                        >
                          <Clock className="w-3.5 h-3.5" />
                          {time}
                        </div>
                        <div
                          className={[
                            "flex items-start gap-3",
                            isLeft ? "md:flex-row-reverse" : "",
                          ].join(" ")}
                        >
                          <div className="shrink-0 w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-display text-xl md:text-2xl font-bold">{title}</h3>
                            <p className="mt-2 text-muted-foreground leading-relaxed">{text}</p>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="mt-16 text-center reveal">
              <Button asChild size="lg" className="text-base px-8 py-6 h-auto">
                <a href={APPLY_URL}>
                  <FileCheck className="w-5 h-5 mr-2" />
                  Jetzt bewerben
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-background">
          <div className="container max-w-3xl">
            <div className="text-center max-w-2xl mx-auto reveal">
              <span className="text-accent font-semibold uppercase tracking-wider text-sm">FAQ</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                Häufige Fragen zur <span className="text-accent">Bewerbung</span>
              </h2>
              <p className="mt-5 text-lg text-muted-foreground">
                Alles, was Du rund um Deine Karriere bei FINABLE wissen solltest.
              </p>
            </div>

            <Accordion type="single" collapsible className="mt-12 reveal">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-border">
                  <AccordionTrigger className="text-left font-display text-lg font-semibold hover:text-accent">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
